import { ReactNode } from 'react';
import { getDateAndTimeFromEpoch } from './CommonUtils';

export interface LabelValueItem {
	label: string;
	value: ReactNode;
}

/**
 * Converts camelCase to Title Case for labels
 * @param str - The camelCase string to convert
 * @returns The Title Case version of the string
 *
 * Examples:
 * - taskName -> "Task Name"
 * - taskExecutionId -> "Task Execution Id"
 * - lastUpdatedBy -> "Last Updated By"
 */
function camelCaseToTitleCase(str: string): string {
	return str
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, (match) => match.toUpperCase())
		.trim();
}

/**
 * Creates an array of label-value objects from data keys with optional transformations
 *
 * @param data - The source data object
 * @param keys - Array of keys from the data object
 * @returns Array of label-value objects
 */
export function createLabelValueArray(
	data: Record<string, unknown>,
	keys: string[]
): LabelValueItem[] {
	return keys.map((key) => {
		let value = data[key] as ReactNode;

		if (isEpoch(data[key])) value = getDateAndTimeFromEpoch(data[key]);
		else if (typeof data[key] === 'object') value = JSON.stringify(data[key]);

		return {
			label: camelCaseToTitleCase(key),
			value,
		};
	});
}

function isEpoch(input: unknown): boolean {
	if (typeof input !== 'number' && typeof input !== 'string') return false;

	const num = typeof input === 'string' ? Number(input) : input;

	if (isNaN(num) || num <= 0) return false;

	const str = String(num);

	return str.length === 10 || str.length === 13;
}

export interface ColumnDefinition {
	id: string;
	header: string;
	cell: (item) => ReactNode;
	sortingField?: string;
	minWidth?: number;
	sortingComparator?: (a: unknown, b: unknown) => number;
}

/**
 * Creates table column definitions from field keys with optional customizations
 * 
 * @param keys - Array of field keys to create columns for
 * @param customColumns - Optional object with custom column configurations
 * @param transforms - Optional object with transform functions for specific keys
 * @returns Array of column definition objects
 * 
 * @example
 * const columns = createColumnDefinitions(
 *   ['subTaskExecutionName', 'taskExecutionId', 'lastUpdatedTime'],
 *   {
 *     subTaskExecutionName: { minWidth: 200 },
 *     taskExecutionId: { sortingField: 'TaskExecutionId' }
 *   },
 *   {
 *     lastUpdatedTime: getDateAndTimeFromEpoch
 *   }
 * );
 */
export function createColumnDefinitions(
	keys: string[],
	customColumns?: Record<string, Partial<ColumnDefinition>>,
	transforms?: Record<string, (value: unknown) => unknown>
): ColumnDefinition[] {
	return keys.map(key => {
		const baseColumn: ColumnDefinition = {
			id: camelCaseToTitleCase(key).replace(/\s+/g, ''),
			header: camelCaseToTitleCase(key),
			cell: (item) => {
				let value = item[key];
				
				if (transforms && transforms[key] && value !== undefined && value !== null) {
					value = transforms[key](value);
				}
				
				if (isEpoch(item[key])) value = getDateAndTimeFromEpoch(item[key]);
				else if (typeof item[key] === 'object') value = JSON.stringify(item[key]);

				return value;
			},
			sortingField: camelCaseToTitleCase(key).replace(/\s+/g, ''),
		};

		if (customColumns && customColumns[key]) {
			return { ...baseColumn, ...customColumns[key] };
		}

		return baseColumn;
	});
}
